import {
  Button,
  DismissibleBanner,
  MessageActionButton,
  MoneyForm,
  QuoteDraftResponse,
  Separator,
} from '@shared/components';
import { useAutoFetchRates } from '@shared/components/money-form/hooks/useAutoFetchRates';
import { CircularProgress } from '@shared/components/plugin-components';
import { useToast } from '@shared/components/toast';
import { CreateCustomerLocation } from '@shared/constants/identifiers';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import {
  ResponseDraftContext,
  ResponseDraftContextProvider,
} from '@shared/contexts/ResponseDraftContext';
import { AutoReplySettingsModal } from '@shared/features/auto-reply-settings/AutoReplySettingsModal';
import { getOriginatorOptions } from '@shared/features/originator-search/getInitialOriginatorOption';
import { OriginatorSearch } from '@shared/features/originator-search/OriginatorSearch';
import { InformationNeeded } from '@shared/features/quote-details/InformationNeeded';
import QuoteReviewForm from '@shared/features/quote-details/QuoteReviewForm';
import {
  JobType,
  Originator,
  QuoteDocument,
  useOnboardNewOriginatorMutation,
  useQuoteQuery,
  useSubmitSubjectOrderMutation,
  useUpdateQuoteMutation,
} from '@shared/generated/graphql';
import useOrganizationSettings from '@shared/graphql/hooks/organization/useOrganizationSettings';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { useFeatureFlagEnabled } from '@shared/hooks/useFeatureFlagEnabled';
import { useGlossary } from '@shared/hooks/useGlossary';
import { useLaneHistory } from '@shared/hooks/useLaneHistory';
import { useQuoteValidation } from '@shared/hooks/useQuoteValidation';
import { useSaveContactCue } from '@shared/hooks/useSaveContactCue';
import { RedactedQuote } from '@shared/plugin/components/RedactedQuote';
import useOriginators from '@shared/plugin/hooks/useOriginators';
import { useNavigateWithReturn } from '@shared/plugin/hooks/useReturnRoute';
import { quoteFromFragment } from '@shared/plugin/types/fromFragments/quote';
import { Quote } from '@shared/types/quote';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import { QueryParamName } from 'clerk_common/constants/urls/queryParams';
import {
  QUOTES_ROUTE,
  SHIPMENTS_ROUTE,
} from 'clerk_common/constants/urls/routes';
import { FeatureFlagName } from 'clerk_common/types';
import clsx from 'clsx';
import { isNil } from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLaneHistoryFilters } from '../../../hooks/useLaneHistoryFilters';
import { useInitiateQuoteOriginatorOnboarding } from '../OriginatorOnboarding/hooks/useInitiateQuoteOriginatorOnboarding';
import { CoverQuote } from './components/CoverQuote';
import { OriginatorTransmitCode } from './components/OriginatorTransmitCode/OriginatorTransmitCode';
import { QuoteLaneHistory } from './components/QuoteLaneHistory/QuoteLaneHistory';
import { RouteSummary } from './components/RouteSummary/RouteSummary';
import QuoteSidePanelHeader from './QuoteSidePanelHeader';
import { getQuoteWarnings } from './utils/getQuoteWarnings';

const POLLING_INTERVAL = 10 * 1000;

const ROOT = makeRootClassName('QuoteSidePanel');
export const el = makeElementClassNameFactory(ROOT);

type OriginatorOption = {
  id: string;
  label: string;
  details: string;
};
interface QuoteSidePanelProps {
  quote: Quote;
  originatorOptions: OriginatorOption[];
  loading: boolean;
  updateOriginator: (originatorId: string) => void;
  originatorsSearchTerm?: string;
  handleOriginatorSearch: (query?: string) => void;
  isEditing?: boolean;
  setIsEditing: (isEditing: boolean) => void;
  webBaseUrl: string;
  backRoute: string;
  disableNewCustomerCreation?: boolean;
}

const QuoteSidePanel = ({
  quote,
  originatorOptions,
  loading,
  updateOriginator,
  handleOriginatorSearch,
  isEditing,
  setIsEditing,
  webBaseUrl,
  backRoute,
  disableNewCustomerCreation,
}: QuoteSidePanelProps) => {
  const { filters, setFilters, resetFilters } = useLaneHistoryFilters(quote);
  const {
    quotes,
    shipments,
    loading: loadingLaneHistory,
  } = useLaneHistory(quote, filters);
  const { validation, loading: loadingValidation } = useQuoteValidation(quote);
  const [onboardNewOriginator] = useOnboardNewOriginatorMutation();
  const { sendToast } = useToast();
  const { track } = useAnalytics();
  const navigateWithReturn = useNavigateWithReturn();
  const scrollRef = useRef<HTMLDivElement>(null);
  const {
    draftReply,
    draftReplyInProgress,
    composeMessage,
    draft,
    composeMessageInProgress,
  } = useContext(ResponseDraftContext);
  const [autoReplySettingsModalOpen, setAutoReplySettingsModalOpen] =
    useState(false);
  const { me, defaultOrgId } = useMeContext();
  const { tms } = useGlossary();
  const { isEnabled: showVoomaCover } = useFeatureFlagEnabled({
    name: FeatureFlagName.SHOW_VOOMA_COVER_FOR_QUOTES,
    userId: me?.id,
    organizationId: defaultOrgId,
  });
  const { isEnabled: showSubjectOrderButton } = useFeatureFlagEnabled({
    name: FeatureFlagName.SEND_MCLEOD_SUBJECT_ORDER,
    userId: me?.id,
    organizationId: defaultOrgId,
  });
  const [submitSubjectOrder, { loading: submittingSubjectOrder }] =
    useSubmitSubjectOrderMutation();

  const handleSubmitSubjectOrder = async () => {
    if (!quote.organization?.id) {
      sendToast('Organization ID not found.', { variant: 'error' });
      return;
    }
    try {
      const { data } = await submitSubjectOrder({
        variables: {
          input: {
            organizationId: quote.organization.id,
            quoteId: quote.id,
          },
        },
      });
      if (data?.submitSubjectOrder.success) {
        sendToast(
          `Subject order ${
            data.submitSubjectOrder.externalOrderId ?? ''
          } sent to ${tms} successfully!`,
          { variant: 'success' }
        );
      } else {
        sendToast(
          data?.submitSubjectOrder.error ?? 'Failed to create subject order',
          { variant: 'error' }
        );
      }
    } catch (e) {
      sendToast(`Error: ${e instanceof Error ? e.message : 'Unknown error'}`, {
        variant: 'error',
      });
    }
  };

  const [isRouteSticky, setIsRouteSticky] = useState(false);
  const routeRef = useRef<HTMLDivElement>(null);
  const warnings = getQuoteWarnings(quote);

  const { initiateOriginatorOnboarding } = useInitiateQuoteOriginatorOnboarding(
    {
      quoteId: quote.id,
    }
  );

  useAutoFetchRates({
    validation,
    quote,
  });

  useEffect(() => {
    const handleScroll = () => {
      if (!routeRef.current || !scrollRef.current) return;
      const formTop = routeRef.current.getBoundingClientRect().top;
      const containerTop = scrollRef.current.getBoundingClientRect().top;
      setIsRouteSticky(formTop < containerTop + 25); // 25px threshold
    };
    const current = scrollRef.current;
    current?.addEventListener('scroll', handleScroll);
    return () => current?.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const shouldDraftReply = new URLSearchParams(window.location.search).get(
      QueryParamName.DRAFT_REPLY
    );
    if (!isNil(shouldDraftReply)) {
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior: 'smooth',
          });
        }
      }, 200);
    }
  }, [scrollRef.current]);

  const sendErrorToast = (e: Error) => {
    sendToast('Something went wrong', {
      description: e.message,
      variant: 'error',
      isDismissible: true,
    });
  };

  const quotingConfig =
    quote.organization.integrationConfigs?.integrations.find(
      (i) => i.orderType === 'QUOTE'
    );
  const hasIntegrationConfig = Boolean(quotingConfig);
  const informationIsMissing = !validation?.valid;

  const createOriginator = async (name: string) => {
    const res = await onboardNewOriginator({
      variables: {
        input: {
          name,
          organizationId: quote.organization.id,
        },
      },
    });
    const orig = res.data?.onboardNewOriginator?.customerFacingOriginator;
    const originatorId = orig?.id;
    track('Clicked Create Customer', {
      originatorName: name,
      originatorId,
      name: CreateCustomerLocation.QUOTE_ORIGINATOR_DROPDOWN,
    });
    if (!originatorId) {
      sendErrorToast(new Error('Could not create originator'));
    } else {
      sendToast(`${orig?.name ?? 'New customer'} successfully created`, {
        variant: 'success',
      });
      initiateOriginatorOnboarding(originatorId);
    }
    return originatorId;
  };

  const openShipment = (shipmentId?: string) => {
    if (!shipmentId) return;
    navigateWithReturn(`${SHIPMENTS_ROUTE}/${shipmentId}`);
  };

  return (
    <div className={el`quote-container`}>
      <div className={el`quote-header`}>
        <QuoteSidePanelHeader
          backRoute={backRoute}
          quote={quote}
          webBaseUrl={webBaseUrl}
        />
        {informationIsMissing && !loadingValidation && <InformationNeeded />}
        <div className="mx-4 mt-2 flex">
          <OriginatorSearch
            handleSearch={handleOriginatorSearch}
            originator={quote.originator as Originator}
            originatorOptions={originatorOptions}
            updateOriginator={updateOriginator}
            loading={loading}
            createOriginator={createOriginator}
            hideCreateOption={disableNewCustomerCreation}
          />
        </div>
        <OriginatorTransmitCode
          quote={quote}
          emptyClassName={isRouteSticky ? 'mb-2' : 'mb-4'}
        />
        <RouteSummary
          expanded={isRouteSticky}
          extractedData={quote.extractedData}
        />
      </div>
      <div
        className={clsx(el`quote-details`, el`shared-quote-details`)}
        ref={scrollRef}
      >
        {warnings.map((warning) => (
          <DismissibleBanner key={warning} variant="warning" title={warning} />
        ))}
        <QuoteReviewForm
          quote={quote}
          isLoading={loading}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          routeRef={routeRef}
        />
        <Separator />
        <QuoteLaneHistory
          webBaseUrl={webBaseUrl}
          quotes={quotes}
          filters={filters}
          quote={quote}
          setFilters={setFilters}
          resetFilters={resetFilters}
          shipments={shipments}
          quoteId={quote.id}
          navigateToQuote={(quoteId: string) =>
            navigateWithReturn(`${QUOTES_ROUTE}/${quoteId}`)
          }
          loading={loadingLaneHistory}
        />
        <Separator />
        {showVoomaCover && (
          <>
            <CoverQuote quote={quote} openShipment={openShipment} />
            <Separator />
          </>
        )}
        <MoneyForm
          quote={quote}
          compact
          hideGetRates={!hasIntegrationConfig}
          webBaseUrl={webBaseUrl}
        />
        {showSubjectOrderButton && (
          <>
            <Separator />
            <div className="flex justify-center px-4 py-3">
              <Button
                variant="primary"
                onPress={handleSubmitSubjectOrder}
                isDisabled={submittingSubjectOrder}
              >
                {submittingSubjectOrder ? 'Sending...' : `Send it to ${tms}`}
              </Button>
            </div>
          </>
        )}
        <Separator />
        <QuoteDraftResponse
          compact
          onEditSettings={() => setAutoReplySettingsModalOpen(true)}
        />
        <AutoReplySettingsModal
          open={autoReplySettingsModalOpen}
          setOpen={setAutoReplySettingsModalOpen}
          quoteIds={[quote.id]}
          originator={{
            id: quote.originator?.id ?? '',
            name: quote.originator?.name ?? '',
          }}
        />
      </div>
      <MessageActionButton
        className="sticky bottom-0 border-t bg-white p-4"
        draftReply={draftReply}
        draftReplyInProgress={draftReplyInProgress}
        composeMessage={composeMessage}
        composeMessageInProgress={composeMessageInProgress}
        draft={draft}
      />
    </div>
  );
};

type QuoteSidePanelContainerProps = {
  webBaseUrl: string;
  backRoute?: string;
};
const QuoteSidePanelContainer = ({
  webBaseUrl,
  backRoute = '/',
}: QuoteSidePanelContainerProps) => {
  const { page } = useAnalytics();
  const { quoteId } = useParams();
  const {
    data,
    loading: loadingQuote,
    stopPolling,
    startPolling,
  } = useQuoteQuery({
    variables: {
      id: quoteId ?? '',
    },
    pollInterval: POLLING_INTERVAL,
    skip: !quoteId,
  });
  const organizationId = data?.quoteById?.organization?.id;
  const [isEditing, setIsEditing] = useState(false);
  const [originatorsSearchTerm, setOriginatorsSearchTerm] = useState<string>();
  const { settings } = useOrganizationSettings({
    id: organizationId ?? '',
    skip: !organizationId,
  });
  const { originators, isLoading: loadingOriginators } = useOriginators({
    organizationId,
    id:
      originatorsSearchTerm === undefined
        ? data?.quoteById?.originator?.id
        : undefined,
    searchTerm: originatorsSearchTerm,
  });
  const originatorOptions = getOriginatorOptions(originators);
  const [updateQuote] = useUpdateQuoteMutation();
  const { handleSendCue } = useSaveContactCue();
  const navigate = useNavigate();

  useEffect(() => {
    if (quoteId) {
      startPolling(POLLING_INTERVAL);
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [quoteId]);

  const handleOriginatorSearch = (query?: string) => {
    setOriginatorsSearchTerm(query);
  };

  const quote = data?.quoteById ? quoteFromFragment(data?.quoteById) : null;

  const updateOriginator = async (originatorId: string) => {
    const id = data?.quoteById?.id;
    if (!id) return;

    const res = await updateQuote({
      variables: {
        input: {
          id,
          originatorId,
        },
      },
      refetchQueries: [{ query: QuoteDocument, variables: { id } }],
      awaitRefetchQueries: true,
    });

    const previousOriginatorId = data?.quoteById?.originator?.id;
    if (!previousOriginatorId) {
      handleSendCue({ job: quote?.job, originatorId, jobType: JobType.QUOTES });
    }

    return res;
  };

  useEffect(() => {
    if (quote?.id) {
      page('Quote', { quoteId: quote?.id });
    }
  }, [quote?.id]);

  const loading = loadingQuote || loadingOriginators;

  // NOTE(parlato): The itemId is the item being replied to - we cannot use
  // the email thread context itemId because it may not be the actual email
  // being replied to.
  const itemId = quote?.job?.emailMetadata?.itemId as string | undefined;
  const originatorId = quote?.originator?.id;

  const isRedacted = !isNil(quote?.job?.redactedAt);
  if (quote && isRedacted) {
    return <RedactedQuote id={quote.id} onClose={() => navigate(-1)} />;
  }

  return (
    <>
      {loading && !quote && (
        <div className="flex h-1/3 w-full items-center justify-center">
          <CircularProgress size="md" />
        </div>
      )}
      {!loading && !quote && <div className="p-2">Could not find quote</div>}
      {quote && (
        <ResponseDraftContextProvider
          quoteIds={[quote.id]}
          originatorId={originatorId}
          itemId={itemId}
          respondToEmail={quote.job?.emailMetadata?.originatorAddress}
        >
          <QuoteSidePanel
            quote={quote as Quote}
            originatorOptions={originatorOptions}
            originatorsSearchTerm={originatorsSearchTerm}
            handleOriginatorSearch={handleOriginatorSearch}
            updateOriginator={updateOriginator}
            loading={loading}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            webBaseUrl={webBaseUrl}
            backRoute={backRoute}
            disableNewCustomerCreation={
              settings?.accessPolicies?.disableNewCustomerCreation ?? false
            }
          />
        </ResponseDraftContextProvider>
      )}
    </>
  );
};

export default QuoteSidePanelContainer;
